<template>
  <section class="p-4">
    <div v-if="loading" id="loading" class="text-center">
      <loading></loading>
    </div>
    <b-row v-else class="mt-3">
      <b-col v-if="studyMilestones.length > 0" class="mb-2" cols="12" lg="6" md="6" sm="12" xl="6">
        <b-card class="cardStyle">
          <section>
            <p class="m-0 milestoneCardLabel">
              <b-icon icon="calendar" scale="1.2" variant="primary"></b-icon>&nbsp; Study
              Milestones:
              {{ studyId }}
            </p>
            <study-milestones :study-milestones="studyMilestones" />
          </section>
        </b-card>
      </b-col>

      <b-col v-if="siteMilestones.length > 0" cols="12" lg="6" md="6" sm="12" xl="6">
        <b-card class="cardStyle">
          <section>
            <p class="m-0 milestoneCardLabel">
              <b-icon icon="building" scale="1.2" variant="primary"></b-icon>&nbsp; Site Milestones:
              {{ studyId }}-{{ siteId }}
            </p>
            <site-milestones :site-milestones="siteMilestones"></site-milestones>
          </section>
        </b-card>
      </b-col>
    </b-row>
  </section>
</template>

<script>
  import moment from 'moment';
  import StudyMilestones from './StudyMilestones';
  import SiteMilestones from './SiteMilestones';

  export default {
    name: 'Milestones',
    components: { StudyMilestones, SiteMilestones },
    metaInfo() {
      let metaTitle = this.$appName + this.studyId + '-' + this.siteId + ' Milestones';
      return {
        title: metaTitle,
      };
    },
    data() {
      return {
        loading: false,
        studyMilestones: [],
        siteMilestones: [],
        studyId: '',
        siteId: '',
        siteMilestonesMap: {
          'Site Enrollment Ready Date': 'Site Activation',
          'Site Initiation Date': 'Site Initiation',
          'Site Activated': 'Site Activation',
          SIV: 'Site Initiation',
          'siv planned(scheduled / Siebel) date': 'Site Initiation',
          // 'Site Activated': 'Site Activation', // this used to exist - not sure if needed
          'Site FSFV Date': 'First Subject First Visit (FSFV)',
          'Site LSLV Date': 'Last Subject Last Visit (LSLV)',
          'Site FSFV': 'First Subject First Visit (FSFV)',
        },
      };
    },
    watch: {
      $route() {
        this.initialize();
      },
    },
    created() {
      this.initialize();
    },
    methods: {
      initialize() {
        this.studyId = this.$route.params['studyId'];
        this.siteId = this.$route.params['siteId'];
        this.getSiteStudyMilestones();
      },
      async getSiteStudyMilestones() {
        this.loading = true;
        let endpoints = [
          `/api/cordis/milestones/${this.studyId}`,
          `/api/get-site-milestones?study=${this.studyId}&site=${this.siteId}`,
        ];
        Promise.allSettled(endpoints.map(endpoint => axios.get(endpoint)))
          .then(
            axios.spread((...allData) => {
              let [study, site] = allData;
              if (study.status === 'fulfilled' && study.value.status !== 204) {
                this.studyMilestones = study.value.data['studyMilestones'];
                this.setupStudyMilestones();
              }
              if (site.status === 'fulfilled' && site.value.status !== 204) {
                this.siteMilestones = site.value.data['items'];
                this.setupSiteMilestones();
              }
              this.loading = false;
            }),
          )
          .catch(error => {
            console.error(error);
            this.loading = false;
          });
      },
      setupSiteMilestones() {
        // Filter out unneeded milestones
        this.siteMilestones = this.siteMilestones.filter(
          milestone => milestone['milestoneName'] in this.siteMilestonesMap,
        );

        // Sort by Actual date
        this.siteMilestones.sort(function(a, b) {
          return new Date(a['actualDate']) - new Date(b['actualDate']);
        });

        // Get Next Planned Date
        this.nextSitePlannedDate();

        // Format Dates & milestone label
        this.siteMilestones.forEach(element => {
          element.plannedDate = this.formatDate(element.plannedDate);
          element.actualDate = this.formatDate(element.actualDate);
          element.baselineDate = this.formatDate(element.baselineDate);
          element.milestoneName = this.siteMilestonesMap[element.milestoneName];
        });

        // Filter out milestones if name is null
        this.siteMilestones = this.siteMilestones.filter(
          element => element['milestoneName'] !== null,
        );
      },
      setupStudyMilestones() {
        // Create custom array of objects
        this.studyMilestones = this.getStudyMilestones();

        // Sort by Planned date
        // this.studyMilestones.sort(function(a, b) {
        //   return new Date(a.planned) - new Date(b.planned);
        // });

        // Get Next Planned Date
        this.nextStudyPlannedDate();

        // Format dates
        this.studyMilestones.forEach(element => {
          element.planned = this.formatDate(element.planned);
          element.actual = this.formatDate(element.actual);
          element.baseline = this.formatDate(element.baseline);
        });
      },
      getStudyMilestones() {
        return [
          {
            label: 'Protocol Approved',
            planned: this.studyMilestones['plannedProtocolApproved'],
            actual: this.studyMilestones['actualProtocolApproved'],
            baseline: this.studyMilestones['baselineProtocolApproved'],
          },
          {
            label: 'First Subject First Visit (FSFV)',
            planned: this.studyMilestones['plannedFSFV'],
            actual: this.studyMilestones['actualFSFV'],
            baseline: this.studyMilestones['baselineFSFV'],
          },
          {
            label: 'Last Subject First Visit (LSFV)',
            planned: this.studyMilestones['plannedLSFV'],
            actual: this.studyMilestones['actualLSFV'],
            baseline: this.studyMilestones['baselineLSFV'],
          },
          {
            label: 'Last Subject Last Visit (LSLV)',
            planned: this.studyMilestones['plannedLSLV'],
            actual: this.studyMilestones['actualLSLV'],
            baseline: this.studyMilestones['baselineLSLV'],
          },
          {
            label: 'Database Release',
            planned: this.studyMilestones['plannedDatabaseRelease'],
            actual: this.studyMilestones['actualDatabaseRelease'],
            baseline: this.studyMilestones['baselineDatabaseRelease'],
          },
          {
            label: 'Study Report Final',
            planned: this.studyMilestones['plannedStudyReportFinal'],
            actual: this.studyMilestones['actualStudyReportFinal'],
            baseline: this.studyMilestones['baselineStudyReportFinal'],
          },
          // {
          //   label: 'Protocol End',
          //   planned: this.studyMilestones['plannedProtocolEnd'],
          //   actual: this.studyMilestones['actualProtocolEnd'],
          //   baseline: this.studyMilestones['baselineProtocolEnd'],
          // },
          // {
          //   label: 'Planned Primary Completion',
          //   planned: this.studyMilestones['plannedPrimaryCompletion'],
          //   actual: this.studyMilestones['actualPrimaryCompletion'],
          //   baseline: this.studyMilestones['baselinePrimaryCompletion'],
          // },

          // {
          //   label: 'Final Tables',
          //   planned: this.studyMilestones['plannedFinalTables'],
          //   actual: this.studyMilestones['actualFinalTables'],
          //   baseline: this.studyMilestones['baselineFinalTables'],
          // },
          // {
          //   label: 'Primary Completion',
          //   planned: this.studyMilestones['plannedPrimaryCompletion'],
          //   actual: this.studyMilestones['actualPrimaryCompletion'],
          //   baseline: this.studyMilestones['baselinePrimaryCompletion'],
          // },
        ];
      },
      formatDate(milestone) {
        if (milestone) {
          return moment(milestone).format('D MMM YYYY');
        }
      },
      nextStudyPlannedDate() {
        const today = new Date();
        let index = this.studyMilestones.findIndex(
          element => element.planned !== '' && moment(element.planned).isAfter(today),
        );
        if (index !== -1) {
          this.studyMilestones.forEach(element => {
            if (moment(element.planned).isBefore(today)) {
              element['isPastDate'] = true;
            }
            this.studyMilestones[index]['isNextDate'] = true;
          });
        }
      },
      nextSitePlannedDate() {
        const today = new Date();
        let index = this.siteMilestones.findIndex(
          element => element['plannedDate'] !== '' && moment(element['plannedDate']).isAfter(today),
        );
        if (index !== -1) {
          this.siteMilestones.forEach(element => {
            element['isPastDate'] = moment(element['plannedDate']).isBefore(today);
            if (element['plannedDate'] == null) {
              element['isPastDate'] = true;
            }
            this.siteMilestones[index]['isNextDate'] = true;
          });
        }
      },
    },
  };
</script>
